import React, { useState } from 'react';
import { Col, Row, getItemWithExpiration } from '../../lib/utils';
import { colors } from '../../lib/colors';
import { CaptionLight, Subtitle1Bold, Body2Regular, Body3Regular } from '../../lib/font';
import ColorSquareButton from '../../components/Button/ColorSquareButton';
import ModalAlert from '../../components/modal/ModalAlert';
import FacilityProductFormModal from '../../components/FacilityProduct/FacilityProductFormModal';
import {
    useFacilityProductList,
    useCreateFacilityProduct,
    useUpdateFacilityProduct,
    useDeleteFacilityProduct,
} from '@/features/facilityProduct/hooks';
import { IFacilityProduct, CreateFacilityProductBody, FacilityProductCategory } from '@/features/facilityProduct/types';
import BusinessInfoFooter from '../../components/BusinessInfoFooter';

const CATEGORY_CONFIG: Record<FacilityProductCategory, { label: string; icon: string; bg: string; border: string; numColor: string; labelColor: string; barColor: string; iconBg: string }> = {
    gym: { label: '헬스(GYM)', icon: '🏋️', bg: '#EFF6FF', border: '#BFDBFE', numColor: '#1E40AF', labelColor: '#3B82F6', barColor: '#3B82F6', iconBg: '#EFF6FF' },
    locker: { label: '락커', icon: '🔐', bg: '#F5F3FF', border: '#DDD6FE', numColor: '#5B21B6', labelColor: '#7C3AED', barColor: '#8B5CF6', iconBg: '#F5F3FF' },
    clothing: { label: '운동복', icon: '👕', bg: '#FFFBEB', border: '#FDE68A', numColor: '#92400E', labelColor: '#D97706', barColor: '#F59E0B', iconBg: '#FFFBEB' },
    etc: { label: '기타', icon: '📦', bg: '#F9FAFB', border: '#E5E7EB', numColor: '#374151', labelColor: '#6B7280', barColor: '#9CA3AF', iconBg: '#F9FAFB' },
};

const CATEGORY_ORDER: FacilityProductCategory[] = ['gym', 'locker', 'clothing'];

const FacilityProductPage = ({ createAlert }: { createAlert: any }) => {
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const centerId = authObject?.centerId;

    // undefined = 전체, true = 판매중, false = 판매중지
    const [statusFilter, setStatusFilter] = useState<boolean | undefined>(true);

    const { data: productData, isLoading } = useFacilityProductList({ centerId });
    const createMutation = useCreateFacilityProduct();
    const updateMutation = useUpdateFacilityProduct();
    const deleteMutation = useDeleteFacilityProduct();

    const [isFormVisible, setIsFormVisible] = useState(false);
    const [editProduct, setEditProduct] = useState<IFacilityProduct | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<IFacilityProduct | null>(null);
    const [showGuide, setShowGuide] = useState(() => localStorage.getItem('guide_facilityProduct') !== 'hidden');

    const allProducts: IFacilityProduct[] = productData?.results ?? [];
    const products: IFacilityProduct[] = statusFilter === undefined
        ? allProducts
        : allProducts.filter((p) => (p.isActive !== false) === statusFilter);

    // 카테고리별 분류
    const groupedProducts = CATEGORY_ORDER.reduce<Record<string, IFacilityProduct[]>>((acc, cat) => {
        const items = products.filter((p: IFacilityProduct) => p.category === cat);
        if (items.length > 0) acc[cat] = items;
        return acc;
    }, {});

    const handleCreate = async (body: CreateFacilityProductBody) => {
        try {
            await createMutation.mutateAsync({ centerId, body });
            setIsFormVisible(false);
            createAlert('상품 등록 완료', '시설 상품이 등록되었습니다.', false);
        } catch {
            createAlert('등록 실패', '상품 등록에 실패했습니다.', true);
        }
    };

    const handleUpdate = async (body: CreateFacilityProductBody & { isActive?: boolean }) => {
        if (!editProduct) return;
        try {
            await updateMutation.mutateAsync({ centerId, productId: editProduct._id, body });
            setEditProduct(null);
            setIsFormVisible(false);
            createAlert('상품 수정 완료', '시설 상품이 수정되었습니다.', false);
        } catch {
            createAlert('수정 실패', '상품 수정에 실패했습니다.', true);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteMutation.mutateAsync({ centerId, productId: deleteTarget._id });
            setDeleteTarget(null);
            createAlert('상품 삭제 완료', '시설 상품이 삭제되었습니다.', false);
        } catch {
            createAlert('삭제 실패', '상품 삭제에 실패했습니다.', true);
        }
    };

    const activeCount = allProducts.filter((p: IFacilityProduct) => p.isActive !== false).length;
    const inactiveCount = allProducts.length - activeCount;

    return (
        <Col
            style={{
                width: '100%',
                height: '100%',
                backgroundColor: '#F8FAFC',
                marginTop: 64,
                overflow: 'auto',
            }}
        >
            <Col className="w-full px-[48px] pt-[36px] pb-[48px]">
                {/* 헤더 */}
                <Row className="justify-between items-center">
                    <Col>
                        <Row className="items-center gap-[8px]">
                            <Subtitle1Bold>시설 상품 관리</Subtitle1Bold>
                            {!showGuide && (
                                <button
                                    onClick={() => { setShowGuide(true); localStorage.removeItem('guide_facilityProduct'); }}
                                    style={{ width: 22, height: 22, borderRadius: '50%', border: '1.5px solid #93C5FD', backgroundColor: '#EFF6FF', color: '#3B82F6', fontSize: 12, fontWeight: 700, cursor: 'pointer', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                    ?
                                </button>
                            )}
                        </Row>
                        <CaptionLight color={colors.LAVEL_1} className="mt-[4px]">
                            총 {products.length}개 상품 · 판매중 {activeCount}개 · 판매중지 {inactiveCount}개
                        </CaptionLight>
                    </Col>
                    <Row className="items-center gap-[12px]">
                        {/* 상태 필터 탭 */}
                        <Row
                            className="border border-[#E1E4E6] rounded-[8px] overflow-hidden"
                            style={{ height: 36 }}
                        >
                            {([
                                { label: '판매중', value: true },
                                { label: '판매중지', value: false },
                                { label: '전체', value: undefined },
                            ] as { label: string; value: boolean | undefined }[]).map((tab, i, arr) => (
                                <button
                                    key={tab.label}
                                    type="button"
                                    onClick={() => setStatusFilter(tab.value)}
                                    style={{
                                        padding: '0 14px',
                                        fontSize: 13,
                                        fontWeight: statusFilter === tab.value ? 700 : 400,
                                        backgroundColor: statusFilter === tab.value ? '#EFF6FF' : 'white',
                                        color: statusFilter === tab.value ? '#1D4ED8' : '#6B7280',
                                        border: 'none',
                                        borderRight: i < arr.length - 1 ? '1px solid #E1E4E6' : 'none',
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                        transition: 'all 0.15s',
                                    }}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </Row>
                        <ColorSquareButton
                            text="+ 상품 등록"
                            size="small"
                            callBack={() => {
                                setEditProduct(null);
                                setIsFormVisible(true);
                            }}
                        />
                    </Row>
                </Row>

                {/* 안내 배너 */}
                {showGuide && (
                    <Col
                        style={{
                            marginTop: 20,
                            padding: '20px 24px',
                            backgroundColor: '#EFF6FF',
                            borderRadius: 12,
                            border: '1px solid #BFDBFE',
                            position: 'relative',
                        }}
                    >
                        <button
                            onClick={() => { setShowGuide(false); localStorage.setItem('guide_facilityProduct', 'hidden'); }}
                            style={{ position: 'absolute', top: 12, right: 16, background: 'none', border: 'none', fontSize: 16, color: '#93C5FD', cursor: 'pointer' }}
                        >
                            ✕
                        </button>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#1E40AF', marginBottom: 8 }}>시설 상품 관리란?</span>
                        <Col style={{ gap: 4, fontSize: 13, color: '#1E40AF', lineHeight: 1.7 }}>
                            <span>헬스, 락커, 운동복 등 <b>시설 이용 상품</b>을 등록하고 관리하는 페이지입니다.</span>
                            <span>여기서 등록한 상품을 기반으로 회원에게 <b>이용권을 발급</b>할 수 있습니다.</span>
                            <span style={{ color: '#7C3AED' }}>그룹수업(GX) 상품은 <b>수강권 관리</b> 메뉴에서 등록해주세요.</span>
                        </Col>
                        <Row style={{ gap: 16, marginTop: 12, fontSize: 12, color: '#3B82F6' }}>
                            <span>1. 상품 등록 → 2. 회원에게 이용권 발급 → 3. 이용권 현황에서 관리</span>
                        </Row>
                    </Col>
                )}

                {/* 카테고리별 KPI 요약 바 */}
                <Row className="mt-[24px] gap-[12px]">
                    {CATEGORY_ORDER.map((cat) => {
                        const cfg = CATEGORY_CONFIG[cat];
                        const count = (groupedProducts[cat] ?? []).length;
                        return (
                            <Col
                                key={cat}
                                className="flex-1 rounded-[12px] px-[20px] py-[16px] relative overflow-hidden"
                                style={{ backgroundColor: cfg.bg, border: `1px solid ${cfg.border}`, minHeight: 72 }}
                            >
                                {/* 왼쪽 컬러 바 */}
                                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, backgroundColor: cfg.barColor, borderRadius: '12px 0 0 12px' }} />
                                <Row className="justify-between items-start">
                                    <Col>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: cfg.labelColor }}>{cfg.label}</span>
                                        <span style={{ fontSize: 28, fontWeight: 800, color: cfg.numColor, lineHeight: 1, marginTop: 4 }}>
                                            {count}<span style={{ fontSize: 13, fontWeight: 500, marginLeft: 2 }}>개</span>
                                        </span>
                                    </Col>
                                    <span className="text-[24px]">{cfg.icon}</span>
                                </Row>
                            </Col>
                        );
                    })}
                </Row>

                {/* 카테고리별 상품 리스트 */}
                <Col className="mt-[32px] gap-[32px]">
                    {isLoading && (
                        <Col className="items-center py-[60px] gap-[12px]">
                            <div className="w-[28px] h-[28px] border-[3px] border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
                            <CaptionLight color={colors.LAVEL_1}>상품 목록을 불러오는 중...</CaptionLight>
                        </Col>
                    )}
                    {!isLoading && Object.entries(groupedProducts).length === 0 && (
                        <Col className="items-center py-[60px]">
                            <span className="text-[48px] mb-[12px]">📦</span>
                            <Body2Regular color={colors.LAVEL_2}>등록된 상품이 없습니다</Body2Regular>
                            <CaptionLight color={colors.LAVEL_1} className="mt-[4px]">
                                상단의 '+ 상품 등록' 버튼으로 첫 상품을 추가해보세요
                            </CaptionLight>
                        </Col>
                    )}

                    {!isLoading && Object.entries(groupedProducts).map(([cat, items]) => {
                        const cfg = CATEGORY_CONFIG[cat as FacilityProductCategory];
                        return (
                            <Col key={cat}>
                                {/* 카테고리 헤더 */}
                                <Row className="items-center gap-[8px] mb-[14px]">
                                    <span
                                        className="w-[32px] h-[32px] rounded-[8px] flex items-center justify-center text-[16px]"
                                        style={{ backgroundColor: cfg.iconBg }}
                                    >
                                        {cfg.icon}
                                    </span>
                                    <Body2Regular style={{ fontWeight: 600 }}>{cfg.label}</Body2Regular>
                                    <span className="text-[12px] text-gray-400 ml-[2px]">
                                        {items.length}개
                                    </span>
                                </Row>

                                {/* 상품 카드 그리드 */}
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                        gap: 14,
                                    }}
                                >
                                    {items.map((p: IFacilityProduct) => (
                                        <Col
                                            key={p._id}
                                            className="bg-white rounded-[14px] p-[20px] pl-[22px] hover:-translate-y-[1px] transition-all cursor-pointer relative overflow-hidden"
                                            style={{
                                                border: `1px solid ${cfg.border}`,
                                                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08), 0 2px 4px rgba(15, 23, 42, 0.04)',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.boxShadow = `0 12px 28px rgba(15,23,42,0.12), 0 0 0 1px ${cfg.barColor}`;
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(15, 23, 42, 0.08), 0 2px 4px rgba(15, 23, 42, 0.04)';
                                            }}
                                            onClick={() => {
                                                setEditProduct(p);
                                                setIsFormVisible(true);
                                            }}
                                        >
                                            {/* 왼쪽 카테고리 컬러 바 */}
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    left: 0,
                                                    top: 0,
                                                    bottom: 0,
                                                    width: 4,
                                                    backgroundColor: cfg.barColor,
                                                }}
                                            />
                                            {/* 상태 */}
                                            <Row className="justify-between items-center mb-[12px]">
                                                <span
                                                    className="px-[8px] py-[3px] rounded-full text-[11px] font-semibold"
                                                    style={{
                                                        backgroundColor: p.isActive !== false ? '#D1FAE5' : '#FEE2E2',
                                                        color: p.isActive !== false ? '#065F46' : '#DC2626',
                                                    }}
                                                >
                                                    {p.isActive !== false ? '● 판매중' : '○ 판매중지'}
                                                </span>
                                                <div />
                                            </Row>

                                            {/* 상품명 */}
                                            <span className="text-[16px] font-bold text-[#1F2937] leading-tight">
                                                {p.name}
                                            </span>

                                            {/* 설명 */}
                                            {p.description && (
                                                <Body3Regular color={colors.LAVEL_1} className="mt-[4px]" style={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    {p.description}
                                                </Body3Regular>
                                            )}

                                            {/* 가격 + 기간 */}
                                            <Row className="mt-[16px] gap-[8px] items-center">
                                                <Col
                                                    className="flex-1 rounded-[8px] py-[10px] px-[12px] items-center"
                                                    style={{ backgroundColor: '#F8FAFC' }}
                                                >
                                                    <span className="text-[10px] text-gray-400 font-medium">가격</span>
                                                    <span className="text-[15px] font-bold text-[#1E40AF] mt-[2px]">
                                                        {p.price.toLocaleString()}원
                                                    </span>
                                                </Col>
                                                <Col
                                                    className="flex-1 rounded-[8px] py-[10px] px-[12px] items-center"
                                                    style={{ backgroundColor: '#F8FAFC' }}
                                                >
                                                    <span className="text-[10px] text-gray-400 font-medium">기간</span>
                                                    <span className="text-[15px] font-bold text-[#1F2937] mt-[2px]">
                                                        {p.duration}일
                                                    </span>
                                                </Col>
                                            </Row>
                                        </Col>
                                    ))}
                                </div>
                            </Col>
                        );
                    })}
                </Col>
            </Col>

            {/* 등록/수정 모달 */}
            <FacilityProductFormModal
                isVisible={isFormVisible}
                onClose={() => {
                    setIsFormVisible(false);
                    setEditProduct(null);
                }}
                onSubmit={editProduct ? handleUpdate : handleCreate}
                onDelete={editProduct ? () => { setIsFormVisible(false); setDeleteTarget(editProduct); } : undefined}
                editProduct={editProduct}
                isLoading={createMutation.isLoading || updateMutation.isLoading}
            />

            {/* 삭제 확인 모달 */}
            {deleteTarget && (
                <ModalAlert
                    modalVisible={!!deleteTarget}
                    setModalVisible={() => setDeleteTarget(null)}
                    title="상품 삭제"
                    content={`'${deleteTarget?.name}' 상품을 삭제하시겠습니까?`}
                    cancelButtonText="취소"
                    confirmButtonText="삭제"
                    callBack={handleDelete}
                    cancelCallBack={() => setDeleteTarget(null)}
                />
            )}

            {/* 사업자 정보 (카드사 심사용) */}
            <div style={{ width: '100%', marginTop: 40 }}>
                <BusinessInfoFooter />
            </div>
        </Col>
    );
};

export default FacilityProductPage;
